import React, { useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { path_url } from "../Config/config";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { faRepeat } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Yup from "yup";
import { useFormik } from "formik";
import SEO from "../components/SEO";

export default function Contact_Us() {
  const generateRandomCaptcha = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let captcha = "";
    for (let i = 0; i < 6; i++) {
      captcha += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    return captcha;
  };

  const [captchaCode, setCaptchaCode] = useState(generateRandomCaptcha());

  const handleReloadCaptcha = () => {
    setCaptchaCode(generateRandomCaptcha());
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must not exceed 50 characters"),
    email: Yup.string()
      .required("Email is required")
      .email("Invalid email format"),
    phonenumber: Yup.string()
      .required("Phone number is required")
      .matches(/^[0-9]{10}$/, "Please enter a valid 10-digit phone number"),
    category: Yup.string().required("Category is required"),
    message: Yup.string()
      .required("Message is required")
      .min(10, "Message must be at least 10 characters"),
    captcha: Yup.string().required("Captcha is required"),
  });

  const showValidationErrors = (errors) => {
    const errorMessages = Object.values(errors);
    if (errorMessages.length > 0) {
      toast.error(errorMessages[0]);
      return false;
    }
    return true;
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phonenumber: "",
      category: "General Inquiry",
      message: "",
      status: "pending",
      captcha: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, resetForm, validateForm }) => {
      const errors = await validateForm(values);
      if (errors && Object.keys(errors).length > 0) {
        showValidationErrors(errors);
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (values.captcha !== captchaCode) {
        toast.error("Incorrect captcha. Please try again.");
        formik.setFieldValue("captcha", "");
        handleReloadCaptcha();
        setSubmitting(false);
        return;
      }
      const { captcha, ...enquiryData } = values;
      try {
        const response = await axios.post(`${path_url}/enquiry`, enquiryData, {
          headers: { "Content-Type": "application/json" },
        });
        if (response.data.statusCodeValue === 200 || response.status === 200) {
          toast.success("Enquiry submitted successfully. We will get back to you soon.");
          resetForm();
          setCaptchaCode(generateRandomCaptcha());
        } else {
          toast.error(response.data.body || "Something went wrong. Please try again later.");
          handleReloadCaptcha();
          formik.setFieldValue("captcha", "");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        if (error.response) {
          toast.error(error.response.data.message || "Server error. Please try again later.");
        } else if (error.request) {
          toast.error("Unable to connect to server. Please check your internet connection.");
        } else {
          toast.error("An error occurred. Please try again.");
        }
        handleReloadCaptcha();
        formik.setFieldValue("captcha", "");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleValidateAndShowErrors = async () => {
    const errors = await formik.validateForm();
    if (errors && Object.keys(errors).length > 0) {
      showValidationErrors(errors);
      const allFields = Object.keys(formik.values);
      allFields.forEach((field) => formik.setFieldTouched(field, true));
    }
  };

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact EnteliPro AI ERP & CRM Support",
    description: "Get in touch with the EnteliPro team for inquiries, support, and sales.",
    url: "https://www.entelipro.com/contact_us",
    mainEntity: {
      "@type": "Organization",
      name: "EnteliPro Solutions",
      email: "support@entelipro.com",
      telephone: "+91 7676289081",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+91 7676289081",
        contactType: "Customer Support",
        availableLanguage: ["English", "Hindi"],
      },
    },
  };

  return (
    <>
      <SEO
        title="Contact Us - AI ERP & CRM Customer Support"
        description="Get in touch with EnteliPro AI ERP and CRM specialists."
        keywords="Contact EnteliPro, AI ERP Support, CRM Customer Service"
        canonical="https://www.entelipro.com/contact_us"
        schema={contactSchema}
      />

      <link rel="stylesheet" href="assets/css/style-starter.css" />
      <link href="https://fonts.googleapis.com/css?family=Nunito:400,600,700&display=swap" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css?family=Muli:400,600,700&display=swap" rel="stylesheet" />

      <style>{`
        .cu-page {
          background: #f0f0f5;
          padding: 50px 0 60px;
        }

        .cu-wrapper {
          display: flex;
          gap: 40px;
          align-items: flex-start;
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 20px;
        }

        /* ── LEFT COLUMN ── */
        .cu-left {
          flex: 0 0 380px;
          padding-top: 10px;
        }

        .cu-eyebrow {
          font-size: 0.78rem;
          font-weight: 700;
          color: #6c2bd9;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          margin-bottom: 12px;
        }

        .cu-heading {
          font-size: 2.4rem;
          font-weight: 800;
          color: #111;
          line-height: 1.15;
          margin-bottom: 16px;
        }

        .cu-subtext {
          font-size: 0.92rem;
          color: #555;
          line-height: 1.7;
          margin-bottom: 32px;
          max-width: 340px;
        }

        .cu-contact-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: #fff;
          border-radius: 10px;
          padding: 14px 18px;
          margin-bottom: 12px;
          box-shadow: 0 1px 6px rgba(0,0,0,0.06);
        }

        .cu-contact-card-icon {
          width: 38px;
          height: 38px;
          background: #f0ebfa;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .cu-contact-card-icon .fa {
          color: #6c2bd9;
          font-size: 1rem;
        }

        .cu-contact-card-label {
          font-size: 0.75rem;
          color: #888;
          margin-bottom: 2px;
        }

        .cu-contact-card-value {
          font-size: 0.92rem;
          font-weight: 700;
          color: #111;
          margin: 0;
        }

        .cu-contact-card a {
          text-decoration: none;
          color: inherit;
        }

        .cu-social-row {
          margin-top: 28px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .cu-social-label {
          font-size: 0.82rem;
          color: #555;
          font-weight: 600;
          margin-right: 4px;
        }

        .cu-social-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #fff;
          border: 1px solid #e0e0e0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #444;
          font-size: 0.85rem;
          text-decoration: none;
          transition: all 0.2s;
        }

        .cu-social-btn:hover {
          background: #6c2bd9;
          color: #fff;
          border-color: #6c2bd9;
        }

        /* ── RIGHT COLUMN (form card) ── */
        .cu-right {
          flex: 1;
          background: #fff;
          border-radius: 14px;
          padding: 32px 30px;
          box-shadow: 0 2px 20px rgba(0,0,0,0.07);
        }

        .cu-field-label {
          font-size: 0.82rem;
          font-weight: 600;
          color: #333;
          margin-bottom: 6px;
          display: block;
        }

        .cu-input {
          width: 100%;
          height: 44px;
          border: 1.5px solid #e4e4e4;
          border-radius: 8px;
          padding: 0 14px;
          font-size: 0.88rem;
          color: #333;
          background: #fafafa;
          transition: border-color 0.2s, box-shadow 0.2s;
          outline: none;
          box-sizing: border-box;
        }

        .cu-input:focus {
          border-color: #6c2bd9;
          box-shadow: 0 0 0 3px rgba(108,43,217,0.1);
          background: #fff;
        }

        .cu-input.is-invalid {
          border-color: #dc3545;
        }

        .cu-textarea {
          width: 100%;
          min-height: 110px;
          border: 1.5px solid #e4e4e4;
          border-radius: 8px;
          padding: 12px 14px;
          font-size: 0.88rem;
          color: #333;
          background: #fafafa;
          resize: vertical;
          transition: border-color 0.2s, box-shadow 0.2s;
          outline: none;
          box-sizing: border-box;
          font-family: inherit;
        }

        .cu-textarea:focus {
          border-color: #6c2bd9;
          box-shadow: 0 0 0 3px rgba(108,43,217,0.1);
          background: #fff;
        }

        .cu-textarea.is-invalid {
          border-color: #dc3545;
        }

        .cu-field {
          margin-bottom: 16px;
        }

        .cu-error {
          font-size: 0.75rem;
          color: #dc3545;
          margin-top: 4px;
          display: block;
        }

        /* Captcha row */
        .cu-captcha-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .cu-captcha-box {
          width: 130px;
          height: 44px;
          background: #f0ebfa;
          border: 1.5px solid #cbb8f0;
          border-radius: 8px;
          font-weight: 700;
          letter-spacing: 4px;
          color: #3b1f6e;
          text-align: center;
          font-size: 1rem;
          flex-shrink: 0;
          cursor: default;
          outline: none;
        }

        .cu-captcha-reload {
          width: 38px;
          height: 38px;
          background: #6c2bd9;
          border: none;
          border-radius: 8px;
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          flex-shrink: 0;
          transition: background 0.2s;
        }

        .cu-captcha-reload:hover {
          background: #5521b5;
        }

        .cu-captcha-input {
          flex: 1;
        }

        /* Submit button */
        .cu-submit-btn {
          width: 100%;
          height: 46px;
          background: #6c2bd9;
          color: #fff;
          border: none;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s;
          margin-top: 8px;
        }

        .cu-submit-btn:hover:not(:disabled) {
          background: #5521b5;
        }

        .cu-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Map */
        .cu-map-wrapper {
          max-width: 1100px;
          margin: 32px auto 0;
          padding: 0 20px;
          border-radius: 12px;
          overflow: hidden;
        }

        .cu-map-wrapper iframe {
          border-radius: 12px;
          display: block;
        }

        /* Responsive */
        @media (max-width: 900px) {
          .cu-wrapper {
            flex-direction: column;
          }
          .cu-left {
            flex: none;
            width: 100%;
          }
          .cu-heading {
            font-size: 1.8rem;
          }
        }

        @media (max-width: 480px) {
          .cu-right {
            padding: 20px 16px;
          }
          .cu-captcha-box {
            width: 100px;
            letter-spacing: 2px;
          }
        }
      `}</style>

      <Navbar />

      {/* Breadcrumb */}
      <section className="w3l-inner-banner-main">
        <div className="about-inner inner2">
          <div className="container seen-w3">
            <ul className="breadcrumbs-custom-path">
              <li>
                <Link to="/">
                  Home <span className="fa fa-angle-right" aria-hidden="true"></span>
                </Link>
              </li>
              <li className="active">Contact Us</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Main Section */}
      <section className="cu-page">
        <div className="cu-wrapper">

          {/* LEFT */}
          <div className="cu-left">
            <p className="cu-eyebrow">Contact Us</p>
            <h1 className="cu-heading">Get in touch today</h1>
            <p className="cu-subtext">
              We love questions and feedback — and we're always happy to help!
              Here are some ways to contact us.
            </p>

            {/* Email card */}
            <div className="cu-contact-card">
              <div className="cu-contact-card-icon">
                <span className="fa fa-envelope" aria-hidden="true"></span>
              </div>
              <div>
                <p className="cu-contact-card-label">Email:</p>
                <a href="mailto:support@entelipro.com">
                  <p className="cu-contact-card-value">support@entelipro.com</p>
                </a>
              </div>
            </div>

            {/* Phone card */}
            <div className="cu-contact-card">
              <div className="cu-contact-card-icon">
                <span className="fa fa-phone" aria-hidden="true"></span>
              </div>
              <div>
                <p className="cu-contact-card-label">Phone:</p>
                <a href="tel:+917676289081">
                  <p className="cu-contact-card-value">+91 7676289081</p>
                </a>
              </div>
            </div>

            {/* Address card */}
            <div className="cu-contact-card">
              <div className="cu-contact-card-icon">
                <span className="fa fa-building" aria-hidden="true"></span>
              </div>
              <div>
                <p className="cu-contact-card-label">Address:</p>
                <p className="cu-contact-card-value" style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                  129, 2nd Floor, Caps Town, Pigdamber,<br />
                  Indore 453331 M.P.
                </p>
              </div>
            </div>

            {/* Social */}
            <div className="cu-social-row">
              <span className="cu-social-label">Reach out to us on:</span>
              <a href="#" className="cu-social-btn" aria-label="Facebook">
                <span className="fa fa-facebook"></span>
              </a>
              <a href="#" className="cu-social-btn" aria-label="Twitter/X">
                <span className="fa fa-twitter"></span>
              </a>
              <a href="#" className="cu-social-btn" aria-label="Instagram">
                <span className="fa fa-instagram"></span>
              </a>
              <a href="#" className="cu-social-btn" aria-label="LinkedIn">
                <span className="fa fa-linkedin"></span>
              </a>
            </div>
          </div>

          {/* RIGHT — Form */}
          <div className="cu-right">
            <form onSubmit={formik.handleSubmit}>

              <div className="cu-field">
                <label className="cu-field-label">Full Name</label>
                <input
                  className={`cu-input ${formik.errors.name && formik.touched.name ? "is-invalid" : ""}`}
                  type="text"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    if (formik.errors.name && formik.touched.name)
                      toast.error(formik.errors.name);
                  }}
                  placeholder="Your name"
                  autoComplete="off"
                />
                {formik.errors.name && formik.touched.name && (
                  <span className="cu-error">{formik.errors.name}</span>
                )}
              </div>

              <div className="cu-field">
                <label className="cu-field-label">Email</label>
                <input
                  className={`cu-input ${formik.errors.email && formik.touched.email ? "is-invalid" : ""}`}
                  type="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    if (formik.errors.email && formik.touched.email)
                      toast.error(formik.errors.email);
                  }}
                  placeholder="Your email address"
                  autoComplete="off"
                />
                {formik.errors.email && formik.touched.email && (
                  <span className="cu-error">{formik.errors.email}</span>
                )}
              </div>

              <div className="cu-field">
                <label className="cu-field-label">Phone Number</label>
                <input
                  className={`cu-input ${formik.errors.phonenumber && formik.touched.phonenumber ? "is-invalid" : ""}`}
                  type="tel"
                  name="phonenumber"
                  value={formik.values.phonenumber}
                  onChange={formik.handleChange}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    if (formik.errors.phonenumber && formik.touched.phonenumber)
                      toast.error(formik.errors.phonenumber);
                  }}
                  placeholder="10-digit phone number"
                  autoComplete="off"
                />
                {formik.errors.phonenumber && formik.touched.phonenumber && (
                  <span className="cu-error">{formik.errors.phonenumber}</span>
                )}
              </div>

              <div className="cu-field">
                <label className="cu-field-label">Select Enquiry</label>
                <select
                  className={`cu-input ${formik.errors.category && formik.touched.category ? "is-invalid" : ""}`}
                  name="category"
                  value={formik.values.category}
                  onChange={formik.handleChange}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    if (formik.errors.category && formik.touched.category)
                      toast.error(formik.errors.category);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <option value="General Inquiry">Select Enquiry</option>
                  <option value="Sales">Sales</option>
                  <option value="Support">Technical Support</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Feedback">Feedback</option>
                </select>
                {formik.errors.category && formik.touched.category && (
                  <span className="cu-error">{formik.errors.category}</span>
                )}
              </div>

              <div className="cu-field">
                <label className="cu-field-label">Leave us a message</label>
                <textarea
                  className={`cu-textarea ${formik.errors.message && formik.touched.message ? "is-invalid" : ""}`}
                  name="message"
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    if (formik.errors.message && formik.touched.message)
                      toast.error(formik.errors.message);
                  }}
                  placeholder="Write your message here..."
                  autoComplete="off"
                ></textarea>
                {formik.errors.message && formik.touched.message && (
                  <span className="cu-error">{formik.errors.message}</span>
                )}
              </div>

              <div className="cu-field">
                <label className="cu-field-label">Captcha Code</label>
                <div className="cu-captcha-row">
                  <input
                    type="text"
                    className="cu-captcha-box"
                    value={captchaCode}
                    readOnly
                  />
                  <button
                    type="button"
                    className="cu-captcha-reload"
                    onClick={handleReloadCaptcha}
                    title="Reload captcha"
                  >
                    <FontAwesomeIcon icon={faRepeat} />
                  </button>
                  <input
                    name="captcha"
                    type="text"
                    placeholder="Enter Captcha"
                    className={`cu-input cu-captcha-input ${formik.errors.captcha && formik.touched.captcha ? "is-invalid" : ""}`}
                    onChange={formik.handleChange}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      if (formik.errors.captcha && formik.touched.captcha)
                        toast.error(formik.errors.captcha);
                    }}
                    value={formik.values.captcha}
                    autoComplete="off"
                  />
                </div>
                {formik.errors.captcha && formik.touched.captcha && (
                  <span className="cu-error">{formik.errors.captcha}</span>
                )}
              </div>

              <button
                type="submit"
                className="cu-submit-btn"
                disabled={formik.isSubmitting}
                onClick={handleValidateAndShowErrors}
              >
                {formik.isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>

        {/* Map */}
        <div className="cu-map-wrapper">
          <iframe
            title="map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3683.073630233274!2d75.79781867530171!3d22.613725479462513!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3962f9a8185778b3%3A0xed49a5f910274fcc!2sENTELIPRO%20AI%20TECHNOLOGIES%20PRIVATE%20LIMITED!5e0!3m2!1sen!2sin!4v1760447061317!5m2!1sen!2sin"
            width="100%"
            height="380"
            allowFullScreen=""
            loading="lazy"
            style={{ border: 0 }}
          ></iframe>
        </div>
      </section>

      <Footer />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}